<!-- The sidebar's Highlights (Seán, 2026-10-22, issues C1, HL1): one speaker at a time, their
     2026 talk's video still at 1080p across the column (Seán: "use the 1080p images"; their
     photo if they have no video), name and talk, then two paragraphs: the talk, then "Bio:" (as
     a video's page); dots above to step, and a swipe. Autoplay (Seán's spec, 2026-10-22): the
     next every 15 s, one pass, switched by its Pause / Autoplay button (WCAG 2.2.2); a dot or a
     swipe stops it; the highlight and the setting in the address (useAutoplay.js,
     useYearSidebar.js). -->
<template>
  <section
    ref="carouselRef"
    class="highlights"
    aria-roledescription="carousel"
    aria-label="Highlights"
    v-on="events">
    <!-- the dots above the image (Seán, 2026-10-22), so they don't move with each entry's
         length; the autoplay's button at their row's end, but first for the keyboard (the
         carousel pattern: reached before the dots, whose focus stops the rotation) -->
    <div class="dots">
      <button
        ref="autoplayButton"
        type="button"
        class="autoplay"
        :class="{isOn}"
        :aria-label="isOn ? 'Pause the highlights' : 'Play the highlights automatically'"
        :style="{'--autoplay-delay': `${AUTOPLAY_DELAY}ms`, '--retract': `${RETRACT_TIME}ms`}"
        @click="toggle">
        <!-- the countdown to the next highlight (Seán's mockup, 2026-10-22): a translucent fill
             behind the label, filling with the timer (cycle), retracting as the next slides in -->
        <span
          v-if="phase !== 'idle'"
          :key="cycle"
          class="countdown"
          :class="phase"
          aria-hidden="true" />
        <span class="label">
          <svg v-if="isOn" viewBox="0 0 10 10" aria-hidden="true">
            <rect x="1.5" y="1" width="2.5" height="8" />
            <rect x="6" y="1" width="2.5" height="8" />
          </svg>
          <svg v-else viewBox="0 0 10 10" aria-hidden="true"><path d="M2 1l7 4-7 4z" /></svg>
          <span v-if="!isCompact">{{ isOn ? "Pause" : "Autoplay" }}</span>
        </span>
      </button>
      <div class="dotList" role="group" aria-label="Choose a highlight">
        <button
          v-for="(item, itemIndex) in items"
          :key="item.name"
          type="button"
          class="dot"
          :aria-label="item.name"
          :aria-current="itemIndex === index ? 'true' : undefined"
          @click="go(itemIndex)" />
      </div>
    </div>
    <!-- the next slides in from the side it lies on, the last out the other way (Seán,
         2026-10-22); at once with reduced motion -->
    <!-- a new slide announced only while not rotating (the carousel pattern) -->
    <div class="slides" :aria-live="isRunning ? 'off' : 'polite'">
      <Transition :name="`slide-${direction}`">
        <article
          :key="current.name"
          aria-roledescription="slide"
          :aria-label="`${getNames(current)}, ${index + 1} of ${items.length}`"
          @touchstart.passive="onTouchStart"
          @touchend="onTouchEnd">
          <!-- the image opens the (first) speaker's dialog, as their name does (Seán, 2026-10-22) -->
          <router-link
            :to="getDetailsLink(getSpeakers(current)[0].name)"
            :aria-label="`About ${getSpeakers(current)[0].name}`"
            class="imageLink">
            <img
              v-if="getStill(current)"
              :src="getStill(current)"
              alt=""
              class="still"
              width="1920"
              height="1080" />
            <img v-else :src="getPhoto(current.photo)" alt="" class="photo" />
          </router-link>
          <!-- run-in headings (Seán, 2026-10-22: to condense): the session and talk, then each
               speaker's name and affiliation, leading their paragraphs -->
          <p>
            <b class="runIn isTalk"
            ><span class="keep"
            ><span v-if="current.session" class="caps session">{{ current.session }}:</span>
              {{ getTalkWords(current)[0] }}</span
            >{{ getTalkWords(current)[1] }}</b
            >{{ " " }}<SidebarText :parts="talkParts" />
          </p>
          <p v-for="speaker in getSpeakers(current)" :key="speaker.name">
            <b class="runIn caps keep isName"
            ><router-link :to="getDetailsLink(speaker.name)">{{ speaker.name }}</router-link></b
            >
            <template v-for="part in getAffiliationParts(speaker.affiliation)" :key="part"
            >{{ " " }}<span class="keep affiliation">{{ part }}</span></template
            >
            {{ " " }}<SidebarText :parts="getItalicParts(speaker.bio)" />
          </p>
          <!-- the talk's video (Seán's spec, 2026-10-28): one outlined button at the entry's end,
               in place of a link on the talk paragraph's words; only when the talk has a video -->
          <router-link v-if="current.video" :to="`/Videos/${current.video[1]}`" class="watch">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4.5v15l12-7.5z" /></svg>
            Watch Talk
          </router-link>
        </article>
      </Transition>
    </div>
  </section>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {useRoute} from "vue-router";
import useYearSidebar, {getHash, getIsSidebarHash, getSlug} from "@/composables/useYearSidebar";
import useSpeakerDialog from "@/composables/useSpeakerDialog";
import useAutoplay, {AUTOPLAY_DELAY, RETRACT_TIME} from "@/composables/useAutoplay";
import {getTitleCase} from "@/composables/useTitleCase";
import SidebarText from "./SidebarText.vue";
import {getPictures, getThumbnailUrl} from "@/components/Video/videoCards";
const props = defineProps({
  items: {type: Array, required: true}, // sidebar.json HIGHLIGHTS
  year: {type: String, required: true},
  selected: {type: String, default: ""}, // the one shown, by its address (#highlights&<speaker>)
  details: {type: String, default: ""} // its speaker dialog open: "details", "details-<person>"
});
// the one shown: the address's (so a reload shows it again), else the first
const index = computed(() => {
  const found = props.items.findIndex((item) => getSlug(item.name) === props.selected);
  return Math.max(found, 0);
});
const direction = ref("next"); // "next": from the right; "prev": from the left
const {isAutoplayRequested: isOn, setHighlights} = useYearSidebar();
// to a highlight by a dot (its side, from where we are) or a swipe (its own direction): a
// deliberate choice, so autoplay stops for good (Seán's spec); the address follows
function go(to, step = to - index.value) {
  if (to === index.value) return;
  direction.value = step > 0 ? "next" : "prev";
  setHighlights({slug: getSlug(props.items[to].name), isAutoplay: false});
}
const current = computed(() => props.items[index.value]);
// a talk's speakers: its own list (two speakers, a paragraph each), else the one
const getSpeakers = ({speakers, name, affiliation, bio}) => speakers || [{name, affiliation, bio}];
// the talk's title: its first word (kept with the session label, "MASTERCLASS: Biomolecular"),
// and the rest, a space before it
function getTalkWords({talk}) {
  const [first, ...rest] = getTitleCase(talk).split(" "); // Title Case (Seán, 2026-10-22)
  return [first, rest.length ? ` ${rest.join(" ")}` : ""];
}
// a speaker's name opens their dialog (as on the People page) here, in the address (Seán,
// 2026-10-22): #highlights&<speaker>&details, &details-<person> in a talk of two; a new entry, so
// Back closes it
const route = useRoute();
function getDetailsLink(name) {
  const isOne = getSpeakers(current.value).length === 1;
  const details = isOne ? "details" : `details-${getSlug(name)}`;
  const autoplay = isOn.value ? "1" : "0"; // kept: closing the dialog leaves it as it was
  const hash = getHash({panel: "highlights", item: getSlug(current.value.name), details, autoplay});
  return {path: route.path, query: route.query, hash};
}
// the speaker the address names, or none
function getDetailsSpeaker(details) {
  if (!details) return "";
  const names = getSpeakers(current.value).map(({name}) => name);
  if (details === "details") return names[0];
  return names.find((name) => `details-${getSlug(name)}` === details) || "";
}
// the dialog follows the address (a link, a reload, Back); closed, the address drops &details
const {speaker: dialogRequest, openSpeaker, closeSpeaker} = useSpeakerDialog();
watch(() => props.details, (details) => {
  const name = getDetailsSpeaker(details);
  if (name) return openSpeaker(name, props.year, null, {keepsAddress: true});
  if (dialogRequest.value?.keepsAddress) closeSpeaker();
}, {immediate: true});
watch(dialogRequest, (request) => {
  if (!request && props.details) setHighlights({slug: getSlug(current.value.name)});
});
// an affiliation, breakable only after its commas: "(TULP Interactive," "Netherlands):"
function getAffiliationParts(affiliation) {
  return `(${affiliation}):`.split(/(?<=,) /);
}
// a text's *…* in italics: "with the *World Bank*" → ["with the ", {i: "World Bank"}, ""]; a
// list's texts the same, its other parts (a link: {href, text}) as they are (SidebarText.vue)
const getTextItalics = (text) => text.split("*")
  .map((piece, pieceIndex) => (pieceIndex % 2 ? {i: piece} : piece));
function getItalicParts(bio) {
  if (!Array.isArray(bio)) return getTextItalics(bio);
  return bio.flatMap((part) => (typeof part === "string" ? getTextItalics(part) : [part]));
}
const getNames = (item) => getSpeakers(item).map(({name}) => name).join(" & ");
// the talk paragraph, plain text (until 2026-10-28 its talkLink words linked to the video; the
// Watch Talk button does now)
const talkParts = computed(() => getItalicParts(current.value.talkText));
const getPhoto = (file) => require(`@/assets/years/${props.year}/People/img/${file}`);
// the video library (loaded once), for each talk's still
const catalogue = ref(null);
fetch("/sitemaps/videos/showcases-lite.json")
  .then((response) => response.json())
  .then((data) => (catalogue.value = data))
  .catch(() => {});
// the talk's video still at 1920×1080 (through the fullstack's thumbnail proxy), or ""
function getStill({video}) {
  if (!catalogue.value || !video) return "";
  const pictures = getPictures(catalogue.value, video[0], video[1]);
  return pictures ? getThumbnailUrl(pictures, "1920x1080") : "";
}
// a swipe left or right (over 40 px) steps, as the dots do
// autoplay (useAutoplay.js): the next every 15 s while wanted (the address's ?autoplay=), held
// while a speaker's dialog is open, or while the address has a hash that isn't the sidebar's (a
// dialog's: #sign-in, #profile…): each step writes the sidebar's hash, which replaced the dialog's
// and closed it mid sign-in (Seán, 2026-10-26)
const isOtherHash = computed(() => Boolean(route.hash) && !getIsSidebarHash(route.hash));
const carouselRef = ref(null);
const getSlugAt = (at) => getSlug(props.items[(at + props.items.length) % props.items.length].name);
const {isRunning, phase, cycle, events, toggle} = useAutoplay({
  isRequested: isOn,
  slug: {current: () => getSlugAt(index.value), next: () => getSlugAt(index.value + 1)},
  target: carouselRef,
  isHeldBy: computed(() => Boolean(props.details) || isOtherHash.value),
  onStart: () => setHighlights({slug: getSlugAt(index.value), isAutoplay: true}),
  onStep: (next) => {
    direction.value = "next";
    setHighlights({slug: next, isAutoplay: true});
  },
  onStop: () => setHighlights({slug: getSlugAt(index.value), isAutoplay: false}),
});
// the autoplay's button fills what the dots leave of the row (Seán, 2026-10-31: eight
// highlights overflowed the 250 px column with the fixed-width button); its word shows when
// the button is wide enough for "Autoplay" (6.8 em), else its icon alone. The width comes from
// the flex layout, not the content, so showing the word changes nothing
const WORDED_WIDTH_EMS = 6.8;
const autoplayButton = ref(null);
const isCompact = ref(false);
let buttonObserver = null;
function fitAutoplayWord() {
  const button = autoplayButton.value;
  if (!button) return;
  const wordedWidth = parseFloat(getComputedStyle(button).fontSize) * WORDED_WIDTH_EMS;
  isCompact.value = button.clientWidth < wordedWidth;
}
onMounted(() => {
  buttonObserver = new ResizeObserver(fitAutoplayWord);
  buttonObserver.observe(autoplayButton.value);
  fitAutoplayWord();
});
onBeforeUnmount(() => buttonObserver?.disconnect());
let startX = 0;
const onTouchStart = (event) => (startX = event.changedTouches[0].clientX);
function onTouchEnd(event) {
  const distance = event.changedTouches[0].clientX - startX;
  if (Math.abs(distance) < 40) return;
  const step = distance < 0 ? 1 : -1;
  go((index.value + step + props.items.length) % props.items.length, step);
}
</script>

<style scoped>
/* the slides: the leaving one held in place above the arriving one while both move */
.slides {
  position: relative;
  overflow: hidden;
}
.slide-next-enter-active,
.slide-next-leave-active,
.slide-prev-enter-active,
.slide-prev-leave-active {
  transition: transform 0.4s ease; /* as the countdown's retraction (useAutoplay RETRACT_TIME) */
}
.slide-next-leave-active,
.slide-prev-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}
.slide-next-enter-from,
.slide-prev-leave-to {
  transform: translateX(100%);
}
.slide-next-leave-to,
.slide-prev-enter-from {
  transform: translateX(-100%);
}
@media (prefers-reduced-motion: reduce) {
  .slide-next-enter-active,
  .slide-next-leave-active,
  .slide-prev-enter-active,
  .slide-prev-leave-active {
    transition: none;
  }
}
/* the image's link: a block, so the image keeps its place; the global focus ring shows */
.imageLink {
  display: block;
}
/* the still across the column, 16:9 */
.still {
  display: block;
  width: 100%;
  max-width: none; /* across the column at every width (global.css caps images) */
  height: auto;
  aspect-ratio: 16 / 9;
  margin-top: 0.6rem;
  object-fit: cover;
  border-radius: 4px;
}
.photo {
  display: block;
  width: 96px;
  margin-top: 0.6rem;
  border-radius: 4px;
}
article > p {
  margin: 0 0 0.5em;
}
article > p:first-of-type {
  margin-top: 0.85em; /* a little more room under the image (Seán, 2026-10-28) */
}
/* a touch more room before each speaker's paragraph (Seán, 2026-10-28) */
article > p:has(.isName) {
  margin-top: 0.85em;
}
/* a paragraph's run-in heading (Seán, 2026-10-22): bold, a touch larger than the text, letters a
   little closer; the session and a speaker's name in capitals, the talk's title as written */
.runIn {
  font-size: 1.04em;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.caps {
  text-transform: uppercase;
}
/* the session's label, before the talk's title: a little smaller, so the title leads (Seán,
   2026-10-22) */
.session {
  font-size: 0.88em;
}
/* the affiliation: muted, so the name and the bio lead (Seán, 2026-10-22); the text's colour
   65% over the background: about 4.8:1 dark, 7:1 light (WCAG AA's 4.5:1) */
.affiliation {
  color: color-mix(in srgb, var(--text-color) 65%, var(--background-color));
}
/* a speaker's name links to their page (Seán, 2026-10-22), in the brand green (2026-10-28):
   darker on the light theme, as the FAQ's open question (global.css --brand-text-dark-share) */
.isName a {
  color: color-mix(in srgb, var(--brand-dark-color) var(--brand-text-dark-share), var(--brand-color));
  text-decoration: none;
}
.isName a:hover {
  text-decoration: underline;
}
/* a speaker's name: the session label's size (1.04 × 0.88), so the paragraph's text leads */
.runIn.isName {
  font-size: 0.915em;
}
/* line breaks (Seán, 2026-10-22): no automatic hyphens (each browser splits words its own way,
   links too); short phrases kept whole: a name, an affiliation, the session and the title's
   first word; and no lone word on a last line where the browser can avoid it (text-wrap:
   pretty: Chrome, Safari; elsewhere lines wrap as usual) */
.keep {
  white-space: nowrap;
}
article > p {
  text-wrap: pretty;
}
/* the talk's: a wider gap before the text, a real space (a line may break there: with the
   margin alone, "Birds" and the text's first word were one, and wrapped together) and a margin */
.runIn.isTalk {
  margin-right: 0.15em;
  font-weight: 900; /* the talk's title heavier than a name (Seán, 2026-10-28; index.html loads it) */
  color: var(--text-color-strong); /* and at full strength, not the text's muted grey (dark mode) */
}
/* Watch Talk (2026-10-28): an outlined button in the brand colour (as the Videos page's Show
   All) across the column, the play icon before the label, a little below the last paragraph */
.watch {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35em;
  box-sizing: border-box;
  width: 100%;
  min-height: 2.25rem; /* near the site's other secondary buttons (Seán, 2026-10-28) */
  margin-top: 1.1rem;
  padding: 0.3em 0.8em 0.3em 0.65em;
  font-size: 0.9em;
  font-weight: 700;
  color: var(--brand-hover-color);
  background: var(--background-color);
  border: 1px solid var(--brand-color);
  border-radius: 0.5rem;
  box-shadow: 0 0.13rem 0.13rem rgba(0, 0, 0, 0.2);
  text-decoration: none;
}
.watch svg {
  width: 0.9em;
  height: 0.9em;
  fill: currentColor;
}
.watch:hover {
  color: white;
  background: var(--brand-hover-color);
}
/* the row: the dots, the autoplay's button at its end */
.dots {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin: 0.4rem 0 0; /* above the image, which has its own top margin */
  padding: 0;
}
/* from the text's left edge (Seán, 2026-10-22): the first dot's mark (9 px, centred in its
   24 px target) starts where the text does, the target reaching 7.5 px into the margin */
/* the targets side by side in the 250 px column (marks 15 px apart), so the button fits the row;
   a little apart where the row is wider (Seán, 2026-10-22: up to 7 px, the marks 31 px apart,
   about 30% more; still one group, never spread across the row) */
.dots {
  container-type: inline-size;
}
.dotList {
  display: flex;
  gap: clamp(0px, (100cqw - 250px) / 10, 7px);
  margin-left: -7.5px;
}
/* the autoplay's button (Seán's mockup, 2026-10-22): playing, "Pause" in a brand-coloured ring;
   paused, "Autoplay" on the control's grey; as tall as a dot's target, so the row keeps its
   height */
.autoplay {
  order: 1; /* shown last, reached first */
  position: relative;
  flex: 1 1 0; /* the rest of the row, whatever the dots leave (Seán, 2026-10-31) */
  min-width: 24px; /* at least a dot's target: the icon alone */
  height: 24px;
  overflow: hidden;
  padding: 0;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1;
  color: color-mix(in srgb, var(--filter-control-text) 80%, var(--filter-control-background));
  background: var(--filter-control-background);
  border: 1px solid var(--filter-control-border);
  border-radius: 6px;
  box-shadow: none;
  cursor: pointer;
}
/* running: the page's colour, a thin brand-coloured border, the label at full strength */
.autoplay.isOn {
  color: var(--filter-control-text);
  background: var(--background-color);
  border-color: var(--brand-color);
}
/* a 44 px tap target (WCAG 2.5.5), the row's height unchanged */
.autoplay::after {
  content: "";
  position: absolute;
  inset: -10px 0;
}
/* the countdown: a translucent brand-coloured fill growing left to right, behind the label, in
   the button's rounded shape; linear, as long as the wait. Full, it retracts right to left as
   the next highlight slides in (Seán, 2026-10-22: 400 ms, easing out), then fills again */
.countdown {
  position: absolute;
  inset: 0;
  border-radius: 5px;
  background: color-mix(in srgb, var(--brand-color) 24%, transparent);
}
.countdown.filling {
  transform-origin: left;
  animation: countdown var(--autoplay-delay) linear forwards;
}
.countdown.retracting {
  transform-origin: left;
  animation: retract var(--retract) ease-out forwards;
}
@keyframes countdown {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
@keyframes retract {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}
.label {
  position: relative; /* above the countdown */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35em;
  width: 100%;
  height: 100%;
}
.autoplay svg {
  width: 0.8em;
  height: 0.8em;
  fill: currentColor;
}
/* a dot: a 24 px target (WCAG 2.5.8) around the 9 px mark; not a button's look */
.dot {
  width: 24px;
  height: 24px;
  padding: 0;
  background: radial-gradient(circle, var(--border-color-subtle) 4.5px, transparent 5px);
  border: none;
  border-radius: 50%;
  box-shadow: none;
  cursor: pointer;
}
.dot[aria-current="true"] {
  background: radial-gradient(circle, var(--brand-color) 4.5px, transparent 5px);
}
.dot:focus-visible {
  outline: 2px solid var(--brand-color);
}
/* hover: a dot that would show another, a step stronger; the current one as it is (DESIGN.md
   § 15) */
.dot:not([aria-current="true"]):hover {
  background: radial-gradient(circle, var(--text-color-less-subtle) 4.5px, transparent 5px);
}

/* Autoplay switches either way: its label at full strength on hover */
.autoplay:hover {
  color: var(--filter-control-text);
}
</style>
